/**
 * Global LC settings with 5-level distribution lever.
 *
 * Updated scale (v2):
 * - New Level 1 equals the previous Level 2 (slightly lean, but not extreme).
 * - New Level 5 is one step more generous than the previous Level 5.
 * - Levels are linearly interpolated between the new min and new max.
 */

// Persisted keys
const LS_LEVEL_KEY = 'lc_level';
const LS_SETTINGS_KEY = 'lc_settings_v2';

// Points conversion used across the app (see test and survey pages)
export const POINTS_PER_LC = 100; // 1 LC = 100 points
export const DEFAULT_DAILY_USER_LIMIT_LC = 2; // Cap per user per day

// Generous (Level 5) baseline values mirrored from current implementation
// These are LC amounts
export const GENEROUS_VALUES = {
  // Creation rewards
  leadCreationLc: 0.07,        // 7 points
  testCreationLc: 0.07,        // 7 points

  // Test awards per correct answer
  basicAnswerLc: 0.005,        // 0.5 points per correct
  intermediateAnswerLc: 0.01,  // 1 point per correct
  advancedAnswerLc: 0.02,      // 2 points per correct

  // Social interactions
  likeGivenLc: 0.003,          // 0.3 points (liker)
  likeReceivedLc: 0.007,       // 0.7 points (author)
  commentGivenLc: 0.006,       // 0.6 points (comment liker)
  commentReceivedLc: 0.014     // 1.4 points (comment author)
};

// The leanest (Level 1) anchors requested by the client
// We only anchor creation rewards explicitly; the rest scale by the same factor
const LEAN_CREATION_LC = 0.001; // For both lead and test creation

// Compute the scale factor for a given level [1..5]
export const getScaleFactorForLevel = (level) => {
  const lvl = Math.min(5, Math.max(1, Number(level || 5)));
  // Base factors from previous scheme
  const baseMin = LEAN_CREATION_LC / GENEROUS_VALUES.leadCreationLc; // ~0.0142857
  const baseMax = 1.0;
  const step = (baseMax - baseMin) / 4; // one level step size
  // New policy: remove old Level 1 (start at old Level 2), add a new Level 5 above old Level 5
  const newMin = baseMin + step;       // old Level 2
  const newMax = baseMax + step;       // one step above old Level 5
  const t = (lvl - 1) / 4;             // 0..1 across five levels
  return newMin + (newMax - newMin) * t;
};

export const computeValuesForLevel = (level) => {
  const f = getScaleFactorForLevel(level);
  const s = GENEROUS_VALUES;
  return {
    level: Number(level || 5),
    // Creation
    leadCreationLc: Number((s.leadCreationLc * f).toFixed(6)),
    testCreationLc: Number((s.testCreationLc * f).toFixed(6)),
    // Tests per answer
    basicAnswerLc: Number((s.basicAnswerLc * f).toFixed(6)),
    intermediateAnswerLc: Number((s.intermediateAnswerLc * f).toFixed(6)),
    advancedAnswerLc: Number((s.advancedAnswerLc * f).toFixed(6)),
    // Social
    likeGivenLc: Number((s.likeGivenLc * f).toFixed(6)),
    likeReceivedLc: Number((s.likeReceivedLc * f).toFixed(6)),
    commentGivenLc: Number((s.commentGivenLc * f).toFixed(6)),
    commentReceivedLc: Number((s.commentReceivedLc * f).toFixed(6))
  };
};

// In-memory cache for DB-first priority
let MEMORY_SETTINGS = null;
const setMemoryFromLevel = (level) => {
  try { MEMORY_SETTINGS = computeValuesForLevel(level); } catch (_) {}
};

export const getActiveLevel = () => {
  try {
    const stored = localStorage.getItem(LS_LEVEL_KEY);
    const lvl = stored ? Number(stored) : 1; // default to lean if nothing stored
    return Math.min(5, Math.max(1, lvl || 1));
  } catch (e) {
    return 1;
  }
};

export const getActiveLcSettings = () => {
  // Prefer live DB-hydrated settings in memory
  if (MEMORY_SETTINGS) {
    // Ensure defaults exist (e.g., daily cap) even if older memory snapshot
    return {
      ...MEMORY_SETTINGS,
      dailyUserLimitLc: typeof MEMORY_SETTINGS.dailyUserLimitLc === 'number'
        ? MEMORY_SETTINGS.dailyUserLimitLc
        : DEFAULT_DAILY_USER_LIMIT_LC
    };
  }
  try {
    const cached = localStorage.getItem(LS_SETTINGS_KEY);
    if (cached) {
      const parsed = JSON.parse(cached);
      if (parsed && parsed.level && parsed.leadCreationLc != null) {
        return {
          ...parsed,
          dailyUserLimitLc: typeof parsed.dailyUserLimitLc === 'number'
            ? parsed.dailyUserLimitLc
            : DEFAULT_DAILY_USER_LIMIT_LC
        };
      }
    }
  } catch (e) {}
  // Fallback: compute from level
  const lvl = getActiveLevel();
  const settings = computeValuesForLevel(lvl);
  const withDefaults = { ...settings, dailyUserLimitLc: DEFAULT_DAILY_USER_LIMIT_LC };
  try {
    localStorage.setItem(LS_SETTINGS_KEY, JSON.stringify(withDefaults));
  } catch (e) {}
  return withDefaults;
};

export const setActiveLevel = (level) => {
  const lvl = Math.min(5, Math.max(1, Number(level || 5)));
  const settings = computeValuesForLevel(lvl);
  try {
    localStorage.setItem(LS_LEVEL_KEY, String(lvl));
    // Preserve any existing non-level fields like dailyUserLimitLc
    const existing = JSON.parse(localStorage.getItem(LS_SETTINGS_KEY) || '{}');
    const merged = {
      ...settings,
      dailyUserLimitLc: typeof existing.dailyUserLimitLc === 'number' ? existing.dailyUserLimitLc : DEFAULT_DAILY_USER_LIMIT_LC
    };
    localStorage.setItem(LS_SETTINGS_KEY, JSON.stringify(merged));
  } catch (e) {}
  return settings;
};

// Helper to convert LC settings to points for UIs that display points
export const lcToPoints = (lc) => Number((lc * POINTS_PER_LC).toFixed(3));

export const getSettingsAsPoints = (settings) => {
  const s = settings || getActiveLcSettings();
  return {
    level: s.level,
    leadCreationPoints: lcToPoints(s.leadCreationLc),
    testCreationPoints: lcToPoints(s.testCreationLc),
    basicAnswerPoints: lcToPoints(s.basicAnswerLc),
    intermediateAnswerPoints: lcToPoints(s.intermediateAnswerLc),
    advancedAnswerPoints: lcToPoints(s.advancedAnswerLc),
    likeGivenPoints: lcToPoints(s.likeGivenLc),
    likeReceivedPoints: lcToPoints(s.likeReceivedLc),
    commentGivenPoints: lcToPoints(s.commentGivenLc),
    commentReceivedPoints: lcToPoints(s.commentReceivedLc)
  };
};

import { FB_URL } from '../store/actions/types';
import axios from 'axios';

// Use the already initialized Firebase instance
import firebase from '../store/firebase'

const DB_PATH = 'platformSettings/lcSettings';

export const fetchSettingsFromDB = async () => {
  if (!firebase || !firebase.database) return null;
  try {
    const snap = await firebase.database.ref(DB_PATH).once('value');
    if (snap.exists()) {
      const val = snap.val();
      if (val && val.level) {
        // Merge DB values into memory and local storage
        const computed = computeValuesForLevel(val.level);
        const full = {
          ...computed,
          dailyUserLimitLc: typeof val.dailyUserLimitLc === 'number' ? val.dailyUserLimitLc : DEFAULT_DAILY_USER_LIMIT_LC
        };
        try { localStorage.setItem(LS_SETTINGS_KEY, JSON.stringify(full)); } catch (_) {}
        try { localStorage.setItem(LS_LEVEL_KEY, String(val.level)); } catch (_) {}
        MEMORY_SETTINGS = full;
        return full;
      }
    }
  } catch (e) {}
  return null;
};

// Live subscription to LC settings. If companyID provided, company override
// takes precedence; otherwise platform settings are used.
export const subscribeSettings = (ctx) => {
  if (!firebase || !firebase.database) return () => {};
  const db = firebase.database;
  const platformRef = db.ref(DB_PATH);
  let companyRef = null;
  let hasCompanyOverride = false;

  const applyFrom = (val) => {
    if (val && typeof val.level === 'number') {
      const computed = computeValuesForLevel(val.level);
      const full = {
        ...computed,
        dailyUserLimitLc: typeof val.dailyUserLimitLc === 'number' ? val.dailyUserLimitLc : DEFAULT_DAILY_USER_LIMIT_LC
      };
      try { localStorage.setItem(LS_SETTINGS_KEY, JSON.stringify(full)); } catch (_) {}
      try { localStorage.setItem(LS_LEVEL_KEY, String(val.level)); } catch (_) {}
      MEMORY_SETTINGS = full;
    }
  };

  const platformHandler = (snap) => {
    if (hasCompanyOverride) return; // company config wins
    if (snap && snap.exists()) applyFrom(snap.val());
  };
  platformRef.on('value', platformHandler);

  if (ctx && ctx.companyID) {
    companyRef = db.ref(`company-settings/${ctx.companyID}/lcSettings`);
    companyRef.on('value', (snap) => {
      if (snap && snap.exists()) {
        hasCompanyOverride = true;
        applyFrom(snap.val());
      } else {
        hasCompanyOverride = false; // fallback to platform
      }
    });
  }

  // Unsubscribe function
  return () => {
    try { platformRef.off('value', platformHandler); } catch (_) {}
    try { if (companyRef) companyRef.off(); } catch (_) {}
  };
};

//const FB_URL = 'https://us-central1-YOUR-PROJECT-ID.cloudfunctions.net'; // Update with your actual project

export const saveSettingsToDB = async (levelOrSettings, ctx) => {
  if (!firebase || !firebase.database) {
    console.warn('[lc_settings] Firebase not available when saving settings');
    return { ok: false, error: 'Firebase not initialized' };
  }

  const s = typeof levelOrSettings === 'number'
    ? computeValuesForLevel(levelOrSettings)
    : levelOrSettings;

  const settingsWithTimestamp = {
    ...s,
    updatedAt: Date.now()
  };

  // Prepare updates object for freshcard function
  const updates = {};

  // Priority 1: Try platform-global
  updates[DB_PATH] = settingsWithTimestamp;

  // Priority 2: If that would fail due to rules, fallback to company-level
  if (ctx?.companyID) {
    updates[`company-settings/${ctx.companyID}/lcSettings`] = settingsWithTimestamp;
  }

  try {
    const res = await axios.post(`${FB_URL}/m115-freshcard`, {
      // const res = await axios.post(`${FB_URL}/freshcard`, {
      params: { updates } // This matches your expected backend structure
    }, {
      timeout: 10000,
      headers: {
        'Content-Type': 'application/json'
      }
    });

    if (res.data?.success === true) {
      setActiveLevel(s.level);
      // Determine which path actually succeeded
      let scope = 'platform';
      let companyID = null;
      if (ctx?.companyID && !DB_PATH.includes('company-settings')) {
        // If we sent both, and platform might have been blocked, assume company worked
        // Note: freshcard applies all valid paths — but we can infer based on context
        scope = 'company';
        companyID = ctx.companyID;
      }

      return { ok: true, scope, companyID };
    } else {
      try { console.error('[lc_settings] freshcard failed:', res.data); } catch (_) {}
      return { ok: false, error: res.data?.error || 'Update failed' };
    }
  } catch (error) {
    try { console.error('[lc_settings] Error calling freshcard function:', error.response?.data || error.message); } catch (_) {}

    // Enhanced error info
    const errMsg = error.response?.data?.error ||
                   error.response?.data?.message ||
                   error.message ||
                   'Network or server error';

    return { ok: false, error: errMsg };
  }
};
/////////////////////////////////////////////////////////////////////////
// export const saveSettingsToDB = async (levelOrSettings, ctx) => {
//   if (!firebase || !firebase.database) {
//     try { console.warn('[lc_settings] Firebase not available when saving settings'); } catch (_) {}
//     return { ok: false, error: 'Firebase not initialized' };
//   }
//   const s = typeof levelOrSettings === 'number' ? computeValuesForLevel(levelOrSettings) : levelOrSettings;
//   const db = firebase.database;
//   // 1) Try platform-global path
//   try {
//     await db.ref(DB_PATH).set({ ...s, updatedAt: Date.now() });
//     setActiveLevel(s.level);
//     return { ok: true, scope: 'platform' };
//   } catch (e1) {
//     try { console.error('[lc_settings] Save to platformSettings failed:', e1 && e1.message ? e1.message : e1); } catch (_) {}
//     // 2) Fallback to company-level if provided
//     try {
//       if (ctx && ctx.companyID) {
//         await db.ref(`company-settings/${ctx.companyID}/lcSettings`).set({ ...s, updatedAt: Date.now() });
//         setActiveLevel(s.level);
//         return { ok: true, scope: 'company', companyID: ctx.companyID };
//       }
//     } catch (e2) {
//       try { console.error('[lc_settings] Save to company-settings failed:', e2 && e2.message ? e2.message : e2); } catch (_) {}
//       return { ok: false, error: e2 && e2.message ? e2.message : String(e2) };
//     }
//     return { ok: false, error: e1 && e1.message ? e1.message : String(e1) };
//   }
// };

export default {
  POINTS_PER_LC,
  GENEROUS_VALUES,
  getActiveLevel,
  getActiveLcSettings,
  setActiveLevel,
  computeValuesForLevel,
  getSettingsAsPoints,
  fetchSettingsFromDB,
  saveSettingsToDB
};


